/**
 * Executions resource - /api/executions endpoints
 */
import { getLogger } from '@qwickapps/logging';
import { ExecutionStatus } from '@qwickforge/api';
const log = getLogger('QwickForge.MiddlewareClient.ExecutionsResource');
export class ExecutionsResource {
    http;
    writeBuffer;
    constructor(http, writeBuffer) {
        this.http = http;
        this.writeBuffer = writeBuffer;
    }
    /**
     * Create a new execution
     */
    async create(sessionId, promptId, agent, type, metadata) {
        const executionId = crypto.randomUUID();
        const now = new Date();
        log.debug('Creating execution', { executionId, sessionId, agent });
        let serverResponse;
        if (this.writeBuffer) {
            // Add to write buffer
            this.writeBuffer.add({
                type: 'execution',
                sessionId,
                executionId,
                data: {
                    id: executionId,
                    sessionId,
                    promptId,
                    agent,
                    type,
                    status: ExecutionStatus.PENDING,
                    startedAt: null,
                    completedAt: null,
                    result: null,
                    summary: null,
                    errorMessage: null,
                    exitCode: null,
                    metadata: metadata || null,
                    createdAt: now.toISOString(),
                },
                timestamp: Date.now(),
            });
        }
        else {
            // Direct write
            serverResponse = await this.http.post('/api/executions', {
                sessionId,
                promptId,
                agent,
                type,
                metadata,
            });
        }
        const actualExecutionId = serverResponse?.execution?.id || executionId;
        log.info('Execution created', { executionId: actualExecutionId, status: ExecutionStatus.PENDING });
        // Return minimal execution object
        return {
            id: actualExecutionId,
            sessionId,
            promptId,
            deviceId: '',
            orgId: '',
            userId: null,
            projectId: null,
            agent,
            type,
            status: ExecutionStatus.PENDING,
            startedAt: null,
            completedAt: null,
            result: null,
            summary: null,
            errorMessage: null,
            exitCode: null,
            diffHash: null,
            artifactUrl: null,
            metadata: metadata || null,
            // Retry support fields
            attempts: 0,
            maxAttempts: 3,
            nextRetryAt: null,
            lastError: null,
            isRetryable: true,
            createdAt: now,
        };
    }
    /**
     * Get execution by ID
     */
    async get(executionId) {
        // Flush write buffer to ensure execution is persisted
        if (this.writeBuffer) {
            await this.writeBuffer.flush();
        }
        try {
            log.debug('Getting execution', { executionId });
            const execution = await this.http.get(`/api/executions/${executionId}`);
            return execution;
        }
        catch (error) {
            if (error.statusCode === 404) {
                log.debug('Execution not found', { executionId });
                return undefined;
            }
            throw error;
        }
    }
    /**
     * List executions for a prompt
     */
    async list(promptId, limit = 50, offset = 0) {
        const params = new URLSearchParams();
        params.set('limit', limit.toString());
        params.set('offset', offset.toString());
        log.debug('Listing executions', { promptId, limit, offset });
        const response = await this.http.get(`/api/prompts/${promptId}/executions?${params.toString()}`);
        log.debug('Executions listed', { count: response.data.length, total: response.total });
        return response;
    }
    /**
     * Atomically claim an execution for processing
     * Returns execution data with cliSessionId and resumeSession flag if claim is successful
     */
    async claim(executionId) {
        try {
            log.debug('Claiming execution', { executionId });
            const response = await this.http.post(`/api/executions/${executionId}/claim`, {});
            log.debug('Execution claimed successfully', {
                executionId,
                cliSessionId: response.cliSessionId,
                resumeSession: response.resumeSession
            });
            return {
                claimed: true,
                execution: response,
                cliSessionId: response.cliSessionId,
                resumeSession: response.resumeSession,
            };
        }
        catch (error) {
            if (error.statusCode === 404) {
                log.debug('Execution not found', { executionId });
                return { claimed: false };
            }
            if (error.statusCode === 409) {
                log.debug('Execution already claimed', { executionId });
                return { claimed: false };
            }
            throw error;
        }
    }
    /**
     * Record agent start
     */
    async recordStart(record) {
        log.debug('Recording agent start', { executionId: record.executionId });
        if (this.writeBuffer) {
            this.writeBuffer.add({
                type: 'execution',
                executionId: record.executionId,
                data: {
                    id: record.executionId,
                    agent: record.agentName,
                    status: ExecutionStatus.RUNNING,
                    startedAt: typeof record.startedAt === 'string'
                        ? record.startedAt
                        : record.startedAt.toISOString(),
                    summary: `Started ${record.agentName}`,
                },
                timestamp: Date.now(),
            });
        }
        else {
            await this.http.patch(`/api/executions/${record.executionId}`, {
                status: ExecutionStatus.RUNNING,
                startedAt: typeof record.startedAt === 'string'
                    ? record.startedAt
                    : record.startedAt.toISOString(),
            });
        }
        log.debug('Agent start recorded', { executionId: record.executionId });
    }
    /**
     * Record agent completion
     */
    async recordCompletion(record) {
        log.debug('Recording agent completion', { executionId: record.executionId });
        if (this.writeBuffer) {
            this.writeBuffer.add({
                type: 'execution',
                executionId: record.executionId,
                data: {
                    id: record.executionId,
                    status: ExecutionStatus.COMPLETED,
                    completedAt: record.completedAt.toISOString(),
                    summary: record.output,
                    result: record.output,
                    metadata: record.metadata,
                },
                timestamp: Date.now(),
            });
        }
        else {
            await this.http.patch(`/api/executions/${record.executionId}`, {
                status: ExecutionStatus.COMPLETED,
                completedAt: record.completedAt.toISOString(),
                result: record.output,
                summary: record.output,
                metadata: record.metadata,
            });
        }
        log.debug('Agent completion recorded', { executionId: record.executionId });
    }
    /**
     * Record agent failure
     */
    async recordFailure(record) {
        log.debug('Recording agent failure', { executionId: record.executionId });
        if (this.writeBuffer) {
            this.writeBuffer.add({
                type: 'execution',
                executionId: record.executionId,
                data: {
                    id: record.executionId,
                    status: ExecutionStatus.FAILED,
                    completedAt: record.completedAt.toISOString(),
                    errorMessage: record.errorMessage,
                    exitCode: record.exitCode || null,
                    summary: `Failed: ${record.errorMessage}`,
                    metadata: record.metadata,
                },
                timestamp: Date.now(),
            });
        }
        else {
            await this.http.patch(`/api/executions/${record.executionId}`, {
                status: ExecutionStatus.FAILED,
                completedAt: record.completedAt.toISOString(),
                errorMessage: record.errorMessage,
                exitCode: record.exitCode || null,
                summary: `Failed: ${record.errorMessage}`,
                metadata: record.metadata,
            });
        }
        log.debug('Agent failure recorded', { executionId: record.executionId });
    }
    /**
     * Mark execution as started
     */
    async markStarted(executionId, startedAt) {
        log.debug('Marking execution as started', { executionId });
        if (this.writeBuffer) {
            this.writeBuffer.add({
                type: 'execution',
                executionId,
                data: {
                    id: executionId,
                    status: ExecutionStatus.RUNNING,
                    startedAt,
                },
                timestamp: Date.now(),
            });
        }
        else {
            await this.http.patch(`/api/executions/${executionId}`, {
                status: ExecutionStatus.RUNNING,
                startedAt,
            });
        }
    }
    /**
     * Mark execution as completed
     */
    async markCompleted(executionId, completedAt, result, summary, metadata) {
        log.debug('Marking execution as completed', { executionId });
        if (this.writeBuffer) {
            this.writeBuffer.add({
                type: 'execution',
                executionId,
                data: {
                    id: executionId,
                    status: ExecutionStatus.COMPLETED,
                    completedAt,
                    result,
                    summary,
                    metadata,
                },
                timestamp: Date.now(),
            });
        }
        else {
            await this.http.patch(`/api/executions/${executionId}`, {
                status: ExecutionStatus.COMPLETED,
                completedAt,
                result,
                summary,
                metadata,
            });
        }
    }
    /**
     * Mark execution as failed
     */
    async markFailed(executionId, completedAt, errorMessage, summary, exitCode) {
        log.debug('Marking execution as failed', { executionId });
        if (this.writeBuffer) {
            this.writeBuffer.add({
                type: 'execution',
                executionId,
                data: {
                    id: executionId,
                    status: ExecutionStatus.FAILED,
                    completedAt,
                    errorMessage,
                    exitCode,
                    summary,
                    isRetryable: false,
                },
                timestamp: Date.now(),
            });
        }
        else {
            await this.http.patch(`/api/executions/${executionId}`, {
                status: ExecutionStatus.FAILED,
                completedAt,
                errorMessage,
                exitCode,
                summary,
                isRetryable: false,
            });
        }
    }
    /**
     * Schedule execution for retry
     */
    async scheduleRetry(executionId, errorMessage, currentAttempts) {
        // Calculate exponential backoff: 2^attempts seconds
        const backoffSeconds = Math.pow(2, currentAttempts);
        const nextRetryAt = new Date(Date.now() + backoffSeconds * 1000);
        log.debug('Scheduling execution retry', { executionId, attempt: currentAttempts + 1 });
        if (this.writeBuffer) {
            this.writeBuffer.add({
                type: 'execution',
                executionId,
                data: {
                    id: executionId,
                    status: ExecutionStatus.RETRYING,
                    attempts: currentAttempts + 1,
                    nextRetryAt: nextRetryAt.toISOString(),
                    lastError: errorMessage,
                    errorMessage,
                },
                timestamp: Date.now(),
            });
        }
        else {
            await this.http.patch(`/api/executions/${executionId}`, {
                status: ExecutionStatus.RETRYING,
                attempts: currentAttempts + 1,
                nextRetryAt: nextRetryAt.toISOString(),
                lastError: errorMessage,
                errorMessage,
            });
        }
        log.info('Execution scheduled for retry', {
            executionId,
            attempt: currentAttempts + 1,
            nextRetryAt: nextRetryAt.toISOString(),
        });
    }
    /**
     * Retry a failed execution
     */
    async retry(executionId) {
        log.debug('Retrying execution', { executionId });
        await this.http.post(`/api/executions/${executionId}/retry`, {});
        log.info('Execution retry requested', { executionId });
    }
}
//# sourceMappingURL=ExecutionsResource.js.map